import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash, FaEnvelope, FaPhone, FaBriefcase, FaBuilding } from "react-icons/fa";
import { Plus } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import UiText from '../components/UiText';
import AddActivityModal from '../components/AddActivityModal';
import RecentActivityTimeline from '../components/RecentActivityTimeline';
import api from '../api/axios';

const getStatusColor = (status) => {
  const normalized = (status || "active").toLowerCase();
  switch (normalized) {
    case "active":
      return "#10b981";
    case "qualified":
      return "#f59e0b";
    case "inactive":
      return "#ef4444";
    default:
      return "#6b7280";
  }
};

const capitalizeStatus = (status) => {
  if (!status) return "Active";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const formatPhoneNumber = (phone) => {
  if (!phone) return "-";
  const cleaned = ("" + phone).replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
};

export default function CustomerDetailsPanel({
  customer,
  onEdit,
  onDelete,
}) {
  const { user } = useAuth();
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [editingActivity, setEditingActivity] = useState(null);
  const [activityError, setActivityError] = useState("");

  const canEditCustomer = (cust) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (cust.owner_id && String(user.id) === String(cust.owner_id)) return true;
    return false;
  };

  useEffect(() => {
    if (customer?.id) {
      fetchActivities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  const fetchActivities = async () => {
    if (!customer?.id) return;
    
    setLoadingActivities(true);
    try {
      const response = await api.get(`/api/customers/${customer.id}/activities/`);
      setActivities(response.data);
    } catch (error) {
      console.error("Error fetching activities:", error);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleSaveActivity = async (activityData) => {
    setActivityError("");
    try {
      if (editingActivity?.id) {
        console.log("Updating activity with data:", activityData);
        const response = await api.patch(
          `/api/customers/${customer.id}/activities/${editingActivity.id}`,
          activityData
        );
        console.log("Activity updated successfully:", response.data);
        setActivities(
          activities.map((act) =>
            act.id === response.data.id ? response.data : act
          )
        );
      } else {
        console.log("Adding activity with data:", {
          ...activityData,
          customer: customer.id,
        });

        const response = await api.post(`/api/customers/${customer.id}/activities/`, {
          ...activityData,
          customer: customer.id,
        });

        console.log("Activity created successfully:", response.data);
        setActivities([response.data, ...activities]);
      }

      setEditingActivity(null);
      setShowActivityModal(false);
    } catch (error) {
      console.error("Full error object:", error);
      console.error("Error response:", error.response);
      console.error("Error message:", error.message);

      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          "Failed to save activity";

      setActivityError(errorMessage);
      setTimeout(() => setActivityError(""), 5000);
    }
  };

  const handleEditActivity = (activity) => {
    setEditingActivity(activity);
    setShowActivityModal(true);
  };

  if (!customer) {
    return (
      <div style={{ 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center", 
        justifyContent: "center",
        height: "100%",
        minHeight: "400px",
        textAlign: "center",
        padding: "2rem"
      }}>
        <div style={{ 
          fontSize: "80px", 
          opacity: "0.15", 
          marginBottom: "1.5rem",
          background: "#2563eb",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}>👤</div>
        <UiText variant="heading" as="h2" style={{ 
          color: "#374151", 
          marginBottom: "0.75rem",
          fontSize: "1.25rem",
          fontWeight: "600"
        }}>
          No Customer Selected
        </UiText>
        <UiText variant="small" as="p" style={{ 
          color: "#9ca3af", 
          lineHeight: "1.6",
          maxWidth: "250px"
        }}>
          Select a customer from the table to view detailed information
        </UiText>
      </div>
    );
  }

  return (
    <div style={{ 
      display: "flex", 
      flexDirection: "column", 
      gap: "0.75rem",
      height: "100%",
      maxHeight: "calc(100vh - 200px)"
    }}>
      {/* Activity Error Message */}
      {activityError && (
        <div style={{
          padding: "0.75rem",
          backgroundColor: "#fee2e2",
          border: "1px solid #fecaca",
          borderRadius: "6px",
          color: "#991b1b",
          fontSize: "0.875rem"
        }}>
          ⚠️ {activityError}
        </div>
      )}
      {/* Header Section */}
      <div style={{ 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center",
        paddingBottom: "0.75rem",
        borderBottom: "2px solid #f3f4f6"
      }}>
        <div style={{ 
          position: "relative",
          marginBottom: "0.5rem"
        }}>
          <div style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            border: "3px solid #e0e7ff",
            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#2563eb",
            fontSize: "36px"
          }}>
            👤
          </div>
        </div>
        <UiText variant="title" as="h2" style={{ 
          fontSize: "1.25rem", 
          marginBottom: "0.125rem",
          color: "#111827",
          fontWeight: "700",
          textAlign: "center"
        }}>
          {customer.name}
        </UiText>
        <UiText variant="small" as="p" style={{ 
          color: "#6b7280",
          fontSize: "0.875rem",
          marginBottom: "0.375rem",
          textAlign: "center"
        }}>
          {customer.position || "No position"}
        </UiText>
        <span
          style={{
            background: getStatusColor(customer.status),
            color: "white",
            fontSize: "0.75rem",
            fontWeight: "600",
            padding: "0.25rem 0.75rem",
            borderRadius: "9999px",
            textTransform: "uppercase",
            letterSpacing: "0.3px",
            display: "inline-block"
          }}
        >
          {capitalizeStatus(customer.status)}
        </span>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: "flex", 
        borderBottom: "2px solid #f3f4f6",
        gap: "1rem",
        paddingBottom: "0"
      }}>
        <button
          onClick={() => setActiveTab("details")}
          style={{
            background: "none",
            border: "none",
            padding: "0.75rem 1rem",
            fontSize: "0.875rem",
            fontWeight: "600",
            cursor: "pointer",
            color: activeTab === "details" ? "#2563eb" : "#6b7280",
            borderBottom: activeTab === "details" ? "3px solid #2563eb" : "3px solid transparent",
            transition: "all 0.2s",
            marginBottom: "-2px"
          }}
        >
          Details
        </button>
        <button
          onClick={() => setActiveTab("activity")}
          style={{
            background: "none",
            border: "none",
            padding: "0.75rem 1rem",
            fontSize: "0.875rem",
            fontWeight: "600",
            cursor: "pointer",
            color: activeTab === "activity" ? "#2563eb" : "#6b7280",
            borderBottom: activeTab === "activity" ? "3px solid #2563eb" : "3px solid transparent",
            transition: "all 0.2s",
            marginBottom: "-2px",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}
        >
          Activity
          {activities.length > 0 && (
            <span style={{
              backgroundColor: "#2563eb",
              color: "white",
              borderRadius: "10px",
              padding: "0.125rem 0.5rem",
              fontSize: "0.75rem",
              fontWeight: "600"
            }}>
              {activities.length}
            </span>
          )}
        </button>
      </div>

      {/* Tab Content - Scrollable */}
      <div style={{ 
        flex: 1, 
        overflowY: "auto",
        overflowX: "hidden",
        minHeight: 0
      }}>
        {activeTab === "details" ? (
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "0"
          }}>
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "0.5rem",
              padding: "0.5rem 0",
              borderBottom: "1px solid #f3f4f6"
            }}>
              <FaBuilding size={18} color="#6b7280" />
              <div style={{ flex: 1 }}>
                <UiText variant="label" as="div" style={{ 
                  fontSize: "0.75rem",
                  color: "#9ca3af",
                  marginBottom: "0.125rem"
                }}>
                  Company
                </UiText>
                <UiText variant="small" as="div" style={{ 
                  color: "#111827",
                  fontWeight: "500"
                }}>
                  {customer.company_name || "Not specified"}
                </UiText>
              </div>
            </div>

            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "0.5rem",
              padding: "0.5rem 0",
              borderBottom: "1px solid #f3f4f6"
            }}>
              <FaBriefcase size={18} color="#6b7280" />
              <div style={{ flex: 1 }}>
                <UiText variant="label" as="div" style={{ 
                  fontSize: "0.75rem",
                  color: "#9ca3af",
                  marginBottom: "0.125rem"
                }}>
                  Position
                </UiText>
                <UiText variant="small" as="div" style={{ 
                  color: "#111827",
                  fontWeight: "500"
                }}>
                  {customer.position || "Not specified"}
                </UiText>
              </div>
            </div>

            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "0.5rem",
              padding: "0.5rem 0",
              borderBottom: "1px solid #f3f4f6"
            }}>
              <FaEnvelope size={18} color="#6b7280" />
              <div style={{ flex: 1 }}>
                <UiText variant="label" as="div" style={{ 
                  fontSize: "0.75rem",
                  color: "#9ca3af",
                  marginBottom: "0.125rem"
                }}>
                  Email
                </UiText>
                {customer.email ? (
                  <UiText variant="small" as="a" 
                    href={`mailto:${customer.email}`}
                    style={{ 
                      color: "#2563eb",
                      fontWeight: "500",
                      textDecoration: "none"
                    }}
                  >
                    {customer.email}
                  </UiText>
                ) : (
                  <UiText variant="small" as="div" style={{ 
                    color: "#111827",
                    fontWeight: "500"
                  }}>
                    Not provided
                  </UiText>
                )}
              </div>
            </div>

            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "0.5rem",
              padding: "0.5rem 0",
              borderBottom: "1px solid #f3f4f6"
            }}>
              <FaPhone size={18} color="#6b7280" />
              <div style={{ flex: 1 }}>
                <UiText variant="label" as="div" style={{ 
                  fontSize: "0.75rem",
                  color: "#9ca3af",
                  marginBottom: "0.125rem"
                }}>
                  Phone
                </UiText>
                <UiText variant="small" as="div" style={{ 
                  color: "#111827",
                  fontWeight: "500"
                }}>
                  {formatPhoneNumber(customer.phone_number) || "Not provided"}
                </UiText>
              </div>
            </div>
          </div>
        ) : (
          /* Activity Tab */
          <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: "1rem", minHeight: 0 }}>
            {/* Add Activity Button */}
            <div>
              <button
                onClick={() => {
                  setEditingActivity(null);
                  setShowActivityModal(true);
                }}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  background: "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s",
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)"
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(37, 99, 235, 0.4)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(37, 99, 235, 0.3)";
                }}
              >
                <Plus size={18} />
                Log Activity
              </button>
            </div>

            {/* Activity Timeline */}
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              <RecentActivityTimeline 
                activities={activities} 
                loading={loadingActivities}
                onEdit={handleEditActivity}
                currentUserId={user?.id}
                isAdmin={user?.role === "admin"}
              />
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons - Fixed at bottom when on Details tab */}
      {activeTab === "details" && canEditCustomer(customer) && (
        <div style={{ display: "flex", gap: "0.5rem", paddingTop: "0.75rem", borderTop: "2px solid #f3f4f6" }}>
          <button
            onClick={() => onEdit(customer)}
            style={{
              flex: 1,
              background: "#2563eb",
              color: "white",
              padding: "8px 12px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "0.95rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              transition: "all 0.2s",
              boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(37, 99, 235, 0.4)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(37, 99, 235, 0.3)";
            }}
          >
            <FaEdit size={16} /> Edit
          </button>
          <button
            onClick={() => onDelete(customer)}
            style={{
              flex: 1,
              background: "#dc2626",
              color: "white",
              padding: "8px 12px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "0.95rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              transition: "all 0.2s",
              boxShadow: "0 4px 12px rgba(245, 87, 108, 0.3)"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(245, 87, 108, 0.4)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(245, 87, 108, 0.3)";
            }}
          >
            <FaTrash size={16} /> Delete
          </button>
        </div>
      )}

      {/* Add Activity Modal */}
      <AddActivityModal
        isOpen={showActivityModal}
        onClose={() => {
          setShowActivityModal(false);
          setEditingActivity(null);
        }}
        onSubmit={handleSaveActivity}
        companyName={customer.name}
        activity={editingActivity}
      />
    </div>
  );
}
